import React, { useState, useEffect } from 'react';
import { api } from '../../api/api';

interface UserAsset {
  id: number;
  asset: {
    id: number;
    name: string;
    description: string;
    imageUrl: string;
    price: string;
  };
}

interface UserProfile {
  id: number;
  name: string;
  email: string;
  assets: UserAsset[];
}

interface SellAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SellAccountModal = ({ isOpen, onClose }: SellAccountModalProps) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const fetchUserProfile = async () => {
        try {
          setLoading(true);
          const response = await api.get<UserProfile>('/api/users/me');
          setProfile(response.data);
        } catch (err: any) {
          setError(err.message || 'Failed to fetch user profile');
        } finally {
          setLoading(false);
        }
      };
      fetchUserProfile();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log({
      description,
      price,
      consent,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 font-gothic">
      <div className="bg-black border-2 border-yellow-400 rounded-lg p-8 w-full max-w-lg m-4">
        <h2 className="text-2xl font-bold text-yellow-400 mb-6">Sell Your Account</h2>

        {loading && <p className="text-yellow-400">Loading account details...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}

        {profile && !loading && !error && (
          <div className="mb-6 p-4 border border-gray-600 rounded-lg bg-gray-800 text-white">
            <h3 className="text-lg font-semibold text-yellow-400 mb-2">Your Account Summary</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <p><strong>Name:</strong> {profile.name}</p>
              <p><strong>Email:</strong> {profile.email}</p>
              <p className="col-span-2"><strong>Total Assets:</strong> {profile.assets.length}</p>
            </div>
            <div className="mt-4">
              <h4 className="text-md font-semibold text-yellow-400 mb-2">Your Assets</h4>
              <div className="max-h-40 overflow-y-auto">
                {profile.assets.map((userAsset) => (
                  <div key={userAsset.id} className="flex items-center mb-2 p-2 bg-gray-700 rounded-lg">
                    <img src={userAsset.asset.imageUrl} alt={userAsset.asset.name} className="w-10 h-10 rounded-md mr-3" />
                    <div>
                      <p className="font-semibold">{userAsset.asset.name}</p>
                      <p className="text-sm text-gray-400">${userAsset.asset.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="description" className="block text-yellow-400 mb-2">Sale Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 rounded-lg bg-gray-800 text-yellow-400 border border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              rows={4}
              placeholder="Describe what's included in this account sale (e.g., rare items, level, etc.)"
              required
            />
          </div>

          <div className="mb-6">
            <label htmlFor="price" className="block text-yellow-400 mb-2">Price (USD)</label>
            <input
              type="number"
              id="price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full p-2 rounded-lg bg-gray-800 text-yellow-400 border border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder="e.g., 150.00"
              required
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="form-checkbox h-5 w-5 text-yellow-400 bg-gray-800 border-yellow-400 focus:ring-yellow-400"
                required
              />
              <span className="ml-3 text-yellow-400">I understand and agree to the terms of service and pricing.</span>
            </label>
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg mr-2 transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              className="gold-button py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!consent || !price || !description || loading}
            >
              List for Sale
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellAccountModal;
